import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import {
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Calendar, Clock, Phone,
  MessageSquare, Bell, RefreshCw, Wallet, BarChart3, Sparkles, Play, Check,
  Users, TrendingUp, Star, FileText, PhoneCall,
} from "lucide-react";
import SmartRecordsStep from "./SmartRecordsStep";

const TOTAL_STEPS = 11;

// ============================================================
// Generic vertical demo template — driven by `cfg` (sector config)
// Used for /demo-estetica and /demo-hair
// ============================================================
const SectorDemoTemplate = ({ cfg }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sessionId] = useState(() => `${cfg.key}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const stepStartRef = useRef(Date.now());

  const track = async (event, extra = {}) => {
    try {
      await axios.post(`${API}/demo/events/track`, {
        session_id: sessionId, variant: cfg.key, event, step,
        duration_ms: Date.now() - stepStartRef.current, data: extra,
      }).catch(() => {});
      axios.post(`${API}/public/landing/track`, {
        event_type: event, landing: `demo-${cfg.key}`, session_id: sessionId,
        meta: { step, ...extra },
      }).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    document.title = cfg.seoTitle;
    track("demo_started", { source: "page_load" });
    // eslint-disable-next-line
  }, []);

  useEffect(() => { stepStartRef.current = Date.now(); }, [step]);

  const next = () => { track("step_completed", { from_step: step }); setStep(s => Math.min(s + 1, TOTAL_STEPS)); };
  const prev = () => setStep(s => Math.max(s - 1, 0));
  const clickTrial = async (source) => {
    await track("clicked_trial", { source, plan: cfg.plan });
    navigate(`/onboarding?sector=${cfg.sectorParam}&prefill=demo-${cfg.key}&plan=${cfg.plan}`);
  };
  const clickCall = async (source) => track("clicked_call", { source });

  const progress = (Math.min(step + 1, TOTAL_STEPS) / TOTAL_STEPS) * 100;
  const P = cfg.palette;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`demo-${cfg.key}-page`}>
      {/* Sticky progress header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to={cfg.landingPath} className="text-xs text-slate-500 hover:text-slate-900 transition-colors hidden sm:flex items-center gap-1" data-testid={`demo-${cfg.key}-back`}>
            <ArrowLeft className="w-3 h-3" /> {cfg.landingLabel}
          </Link>
          <div className="flex-1 flex items-center gap-3">
            <span className="text-xs text-slate-500 font-semibold whitespace-nowrap" data-testid={`demo-${cfg.key}-step-label`}>
              {Math.min(step + 1, TOTAL_STEPS)}/{TOTAL_STEPS}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${P.grad} transition-all duration-500`} style={{ width: `${progress}%` }} data-testid={`demo-${cfg.key}-progress`} />
            </div>
            <span className="text-[10px] text-slate-400 hidden md:inline">Demo completa in ~2 min</span>
          </div>
          <Button onClick={() => clickTrial("header")} className={`rounded-full bg-gradient-to-r ${P.grad} ${P.gradHover} text-white text-xs px-4 h-8`} data-testid={`demo-${cfg.key}-header-cta`}>
            Prova gratis 14gg
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {step === 0 && <StepCover cfg={cfg} next={next} clickTrial={clickTrial} />}
        {step === 1 && <StepProblem cfg={cfg} />}
        {step === 2 && <SmartRecordsStep sector={cfg.key} onCta={() => clickTrial("smart_records_step")} />}
        {step === 3 && <StepCalendar cfg={cfg} />}
        {step === 4 && <StepBooking cfg={cfg} />}
        {step === 5 && <StepWhatsApp cfg={cfg} />}
        {step === 6 && <StepReminder cfg={cfg} />}
        {step === 7 && <StepRecall cfg={cfg} />}
        {step === 8 && <StepPackages cfg={cfg} />}
        {step === 9 && <StepAnalytics cfg={cfg} />}
        {step === 10 && <StepClosing cfg={cfg} clickTrial={clickTrial} clickCall={clickCall} />}

        {step > 0 && step < 10 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button variant="ghost" onClick={prev} className="text-slate-500 hover:text-slate-900 rounded-full" data-testid={`demo-${cfg.key}-prev`}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            <Button onClick={next} className={`rounded-full bg-gradient-to-r ${P.grad} ${P.gradHover} text-white shadow-lg`} data-testid={`demo-${cfg.key}-next`}>
              {step === 9 ? "Vedi i risultati" : "Avanti"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- STEP 0 — Cover ----------
const StepCover = ({ cfg, next, clickTrial }) => {
  const P = cfg.palette;
  return (
  <div className="text-center py-8 sm:py-16 animate-[fadeIn_0.6s_ease]" data-testid={`demo-${cfg.key}-step-0`}>
    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full ${P.bg} border ${P.border} ${P.text} text-xs font-semibold mb-6`}>
      <Sparkles className="w-3 h-3" /> {cfg.cover.badge}
    </div>
    <h1 className="text-4xl sm:text-6xl font-black mb-6 leading-tight tracking-tight">
      {cfg.cover.h1Pre}<br />
      <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>{cfg.cover.h1Highlight}</span>
    </h1>
    <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">{cfg.cover.sub}</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mb-10">
      {[
        { icon: Calendar,      label: "Agenda" },
        { icon: MessageSquare, label: "WhatsApp" },
        { icon: Bell,          label: "Reminder" },
        { icon: RefreshCw,     label: "Recall" },
      ].map((c, i) => (
        <div key={i} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <div className={`w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br ${P.grad} flex items-center justify-center shadow-md`}>
            <c.icon className="w-5 h-5 text-white" />
          </div>
          <p className="text-xs font-semibold">{c.label}</p>
        </div>
      ))}
    </div>
    <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-md mx-auto">
      <Button onClick={next} size="lg" className={`w-full sm:w-auto rounded-full bg-gradient-to-r ${P.grad} ${P.gradHover} text-white px-8 h-12 shadow-xl`} data-testid={`demo-${cfg.key}-start`}>
        <Play className="w-4 h-4 mr-2" /> Inizia la demo
      </Button>
      <Button onClick={() => clickTrial("cover")} size="lg" variant="outline" className={`w-full sm:w-auto rounded-full border-2 ${P.border.replace("border-","border-")} ${P.text} hover:${P.bg} px-8 h-12`} data-testid={`demo-${cfg.key}-cover-trial`}>
        Prova gratis 14 giorni →
      </Button>
    </div>
    <div className="mt-10 flex items-center justify-center gap-6 text-xs text-slate-500">
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Nessuna carta</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Setup 3 minuti</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Supporto in italiano</span>
    </div>
  </div>
  );
};

// ---------- STEP 1 — Problem ----------
const StepProblem = ({ cfg }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-1`}>
    <div className="text-center mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold mb-4">
        <AlertTriangle className="w-3 h-3" /> 1/10 · Il problema che vivi ogni giorno
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Ogni settimana <span className="text-rose-600">perdi ore e soldi</span></h2>
      <p className="text-slate-600 max-w-2xl mx-auto">Non è colpa tua. È un sistema disordinato che ti frena e ti fa perdere clienti.</p>
    </div>
    <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
      {cfg.pains.map((p, i) => (
        <div key={i} className="p-4 rounded-2xl bg-rose-50 border border-rose-200" data-testid={`demo-${cfg.key}-pain-${i}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-rose-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900">{p.text}</p>
              <p className="text-xs text-rose-700 mt-1 font-medium">{p.loss}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-10 text-center p-5 rounded-2xl bg-slate-900 text-white max-w-2xl mx-auto">
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-bold">Il conto finale</p>
      <p className="text-2xl font-black text-rose-400">{cfg.painFinal.value} di fatturato perso</p>
      <p className="text-xs text-slate-400 mt-2">{cfg.painFinal.note}</p>
    </div>
  </div>
);

// ---------- STEP 2 — Calendar (live booking simulation) ----------
const StepCalendar = ({ cfg }) => {
  const P = cfg.palette;
  const [liveAppt, setLiveAppt] = useState(null);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => { setLiveAppt(cfg.liveBooking); setFlash(true); }, 2500);
    const t2 = setTimeout(() => setFlash(false), 5500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [cfg]);

  const allTimes = Array.from(new Set([...cfg.appointments.map(a => a.time), cfg.liveBooking.time])).sort();

  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-2`}>
    <div className="text-center mb-8">
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${P.bg} border ${P.border} ${P.text} text-xs font-semibold mb-4`}>
        <Calendar className="w-3 h-3" /> 2/10 · La tua agenda, finalmente chiara
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Tutti gli operatori. Tutte le {cfg.rooms.length === 3 && cfg.rooms[0].toLowerCase().includes("postazione") ? "postazioni" : "cabine"}. <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>Un colpo d'occhio.</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">Vedi all'istante chi è libero, chi è occupato e dove sistemare un cliente — senza più mail, telefonate e post-it.</p>
    </div>
    <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden max-w-5xl mx-auto relative">
      <div className={`absolute top-14 right-4 z-20 transform transition-all duration-500 ${flash ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"}`} data-testid={`demo-${cfg.key}-live-booking`}>
        <div className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold shadow-xl flex items-center gap-2 animate-pulse">
          <span className="w-1.5 h-1.5 rounded-full bg-white" />
          {cfg.liveBooking.label}
        </div>
      </div>
      <div className="bg-slate-50 p-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-700">Martedì 13 Maggio</span>
        <span className="text-xs text-slate-500">· {cfg.operators.length} operatori · {cfg.rooms.length} {cfg.rooms[0].toLowerCase().includes("postazione") ? "postazioni" : "spazi"}</span>
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE · {liveAppt ? "99%" : "97%"} riempimento
        </span>
      </div>
      <div className="p-3 sm:p-4 overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="text-xs text-slate-400 font-semibold">Ora</div>
            {cfg.rooms.map((r, i) => <div key={i} className="text-xs font-bold text-slate-700">{r}</div>)}
          </div>
          {allTimes.map(time => (
            <div key={time} className="grid grid-cols-4 gap-2 mb-1.5 items-stretch">
              <div className="text-xs text-slate-500 font-semibold py-2">{time}</div>
              {[0, 1, 2].map(room => {
                const fromStatic = cfg.appointments.find(a => a.time === time && a.room === room);
                const fromLive = liveAppt && liveAppt.time === time && liveAppt.room === room ? liveAppt : null;
                const ap = fromStatic || fromLive;
                if (!ap) return <div key={room} className="h-12 rounded-lg border border-dashed border-slate-200 bg-slate-50/50" />;
                const op = cfg.operators[ap.op];
                const isLive = !!fromLive;
                return (
                  <div key={room} className={`p-2 rounded-lg border-l-4 ${ap.color} text-[11px] transition-all ${isLive ? "animate-[fadeInScale_0.5s_ease] ring-2 ring-emerald-400 ring-offset-1 shadow-lg" : ""}`}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${op.color}`} />
                      <span className="text-slate-500 text-[10px]">{op.role}</span>
                      {isLive && <span className="ml-auto text-[8px] font-bold text-emerald-700 uppercase tracking-wider">NEW</span>}
                    </div>
                    <p className="font-bold text-slate-900 truncate">{ap.service}</p>
                    <p className="text-slate-600 truncate">{ap.patient}</p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-emerald-50 border-t border-emerald-200 p-3 text-xs text-emerald-800 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" />
        <span className="font-semibold">Drag & drop</span> per spostare un appuntamento, <span className="font-semibold">click</span> per aprire la scheda cliente.
      </div>
    </div>
  </div>
  );
};

// ---------- STEP 3 — Smart Page Booking ----------
const StepBooking = ({ cfg }) => {
  const P = cfg.palette;
  const Icon = cfg.smartPage.logoIcon;
  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-3`}>
    <div className="text-center mb-8">
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${P.bg} border ${P.border} ${P.text} text-xs font-semibold mb-4`}>
        <Calendar className="w-3 h-3" /> 3/10 · Prenotazioni online 24/7
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Il tuo {cfg.key === "hair" ? "salone" : "centro"} prenotabile <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>anche di notte</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">Ogni attività riceve una <strong>Smart Page pubblica</strong> professionale: i clienti scelgono servizio, giorno e orario — tu ricevi tutto già in agenda.</p>
    </div>
    <div className="max-w-4xl mx-auto bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden">
      <div className="bg-slate-100 p-2 border-b border-slate-200 flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="flex-1 text-xs font-mono text-slate-500 bg-white rounded px-2 py-0.5 border border-slate-200 truncate">
          smartbooker.it/{cfg.smartPage.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
        </div>
      </div>
      <div className="p-5 sm:p-8">
        <div className="text-center mb-6">
          <div className={`inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br ${P.grad} items-center justify-center mb-3 shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold">{cfg.smartPage.brand}</h3>
          <p className="text-xs text-slate-500">{cfg.smartPage.tagline}</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
            <span className="text-xs text-slate-500 ml-1">4.9 · {cfg.smartPage.reviews} recensioni</span>
          </div>
        </div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Scegli il servizio</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-5">
          {cfg.services.slice(0, 4).map((s, i) => (
            <div key={i} className={`p-3 rounded-xl border-2 ${s.highlight ? `${P.border} ${P.bg}` : "border-slate-200"} flex items-center justify-between`} data-testid={`demo-${cfg.key}-service-${i}`}>
              <div>
                <p className="font-bold text-sm">{s.name}</p>
                <p className="text-xs text-slate-500">{s.duration} min</p>
              </div>
              <p className="font-black text-sm">€{s.price}</p>
            </div>
          ))}
        </div>
        <Button className={`w-full rounded-xl bg-gradient-to-r ${P.grad} ${P.gradHover} text-white h-12 font-bold shadow-lg`}>
          Prenota ora <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  </div>
  );
};

// ---------- STEP 4 — WhatsApp ----------
const StepWhatsApp = ({ cfg }) => {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      while (mounted) {
        setStage(0); await new Promise(r => setTimeout(r, 1400));
        if (!mounted) return; setStage(1); await new Promise(r => setTimeout(r, 2400));
        if (!mounted) return; setStage(2); await new Promise(r => setTimeout(r, 1100));
        if (!mounted) return; setStage(3); await new Promise(r => setTimeout(r, 3500));
        if (!mounted) return; setStage(4); await new Promise(r => setTimeout(r, 600));
      }
    };
    run();
    return () => { mounted = false; };
  }, []);
  const m = cfg.waMessage;
  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-4`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
        <MessageSquare className="w-3 h-3" /> 4/10 · WhatsApp automatico
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Conferma istantanea. <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Dove la cliente la legge davvero.</span></h2>
      <p className="text-slate-600 max-w-2xl mx-auto">Appena un cliente prenota, riceve un messaggio <strong>WhatsApp</strong> di conferma personalizzato — non una mail che finisce nello spam.</p>
    </div>
    <div className="max-w-md mx-auto">
      <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-t-3xl p-4 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><cfg.smartPage.logoIcon className="w-5 h-5" /></div>
        <div className="flex-1">
          <p className="font-bold text-sm">{cfg.smartPage.brand}</p>
          <p className="text-[10px] opacity-80">{stage === 0 ? "sta scrivendo..." : "online"}</p>
        </div>
      </div>
      <div className="bg-[#e5ddd5] dark:bg-slate-800 p-4 space-y-2 min-h-[360px]">
        {stage === 0 && (
          <div className="bg-white rounded-2xl rounded-tl-none p-3 w-16 shadow-sm">
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:"0ms"}} />
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:"150ms"}} />
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:"300ms"}} />
            </div>
          </div>
        )}
        {stage >= 1 && (
          <div className="bg-white rounded-2xl rounded-tl-none p-3 max-w-[85%] shadow-sm animate-[fadeInScale_0.4s_ease]">
            <p className="text-sm text-slate-900 leading-relaxed">
              ✅ <strong>Ciao {m.patientName}!</strong><br /><br />
              Il tuo appuntamento è confermato:<br />
              📅 <strong>{m.day}</strong><br />
              💆 {m.service} — {m.duration} min<br />
              👤 {m.operator}<br /><br />
              📍 {m.address}<br /><br />
              Se hai bisogno di modificare, rispondi qui.
            </p>
            <p className="text-[10px] text-slate-400 text-right mt-1">09:42 ✓✓</p>
          </div>
        )}
        {stage === 2 && (
          <div className="flex justify-end">
            <div className="bg-emerald-200 rounded-2xl rounded-tr-none p-3 w-16 shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{animationDelay:"0ms"}} />
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{animationDelay:"150ms"}} />
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{animationDelay:"300ms"}} />
              </div>
            </div>
          </div>
        )}
        {stage >= 3 && (
          <div className="flex justify-end animate-[fadeInScale_0.4s_ease]">
            <div className="bg-emerald-200 rounded-2xl rounded-tr-none p-3 max-w-[80%] shadow-sm">
              <p className="text-sm text-slate-900">Grazie! 🙏 A domani</p>
              <p className="text-[10px] text-slate-500 text-right mt-1">09:43 ✓✓</p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-b-3xl border border-t-0 border-slate-200 p-3 text-center">
        <p className="text-xs text-slate-600">
          <span className="font-bold text-emerald-600">+ 32% conferme</span> rispetto alla sola email
        </p>
      </div>
    </div>
  </div>
  );
};

// ---------- STEP 5 — Reminder ----------
const StepReminder = ({ cfg }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-5`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold mb-4">
        <Bell className="w-3 h-3" /> 5/10 · Reminder automatici
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3"><span className="text-rose-600">-70% no-show</span>. Senza muovere un dito.</h2>
      <p className="text-slate-600 max-w-2xl mx-auto">24 e 2 ore prima dell'appuntamento, SmartBooker manda <strong>promemoria automatici</strong> via WhatsApp. Il cliente può confermare o spostare con un tap.</p>
    </div>
    <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-4">
      {[
        { hours: 24, title: "24h prima", color: "from-sky-500 to-blue-600",
          msg: `🔔 Ciao ${cfg.waMessage.patientName.split(" ")[0]}, ti ricordo il tuo appuntamento di domani con ${cfg.waMessage.operator.split(" ")[0]}. Conferma con un tap 👇`,
          cta: "Confermo ✅  /  Sposta 🔄" },
        { hours: 2, title: "2h prima", color: "from-violet-500 to-purple-600",
          msg: `⏰ Tra 2 ore ti aspettiamo in ${cfg.waMessage.address.split(",")[0]}. A dopo!`,
          cta: "Vedi mappa 📍" },
      ].map((r, i) => (
        <div key={i} className="rounded-3xl overflow-hidden border-2 border-slate-200 bg-white shadow-lg">
          <div className={`bg-gradient-to-r ${r.color} p-3 text-white`}>
            <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Reminder</p>
            <p className="font-bold">{r.title}</p>
          </div>
          <div className="p-4">
            <div className="bg-[#e5ddd5] rounded-2xl p-3 mb-3"><p className="text-sm text-slate-900 leading-relaxed">{r.msg}</p></div>
            <p className="text-xs text-slate-500 text-center">{r.cta}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 max-w-3xl mx-auto p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-center">
      <p className="text-xs uppercase tracking-wider opacity-80 mb-2 font-bold">Risultato medio</p>
      <div className="grid grid-cols-3 gap-4">
        <div><p className="text-3xl font-black">-70%</p><p className="text-xs opacity-80">No-show</p></div>
        <div><p className="text-3xl font-black">+32%</p><p className="text-xs opacity-80">Conferme</p></div>
        <div><p className="text-3xl font-black">€600</p><p className="text-xs opacity-80">Recuperati/mese</p></div>
      </div>
    </div>
  </div>
);

// ---------- STEP 6 — Recall ----------
const StepRecall = ({ cfg }) => {
  const P = cfg.palette;
  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-6`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-4">
        <RefreshCw className="w-3 h-3" /> 6/10 · {cfg.recallTitle}
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Riattivi chi non torna <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>senza chiamare nessuno</span></h2>
      <p className="text-slate-600 max-w-2xl mx-auto">SmartBooker li riattiva in automatico con messaggi personalizzati al momento giusto.</p>
    </div>
    <div className="max-w-3xl mx-auto bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden">
      <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-white" /></div>
        <div>
          <p className="font-bold">Campagna recall attiva</p>
          <p className="text-xs text-slate-600">{cfg.recallSubtitle}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-2xl font-black text-amber-600">{cfg.inactivePatients.length}</p>
          <p className="text-[10px] uppercase text-slate-500">in coda</p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {cfg.inactivePatients.map((p, i) => (
          <div key={i} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`demo-${cfg.key}-recall-${i}`}>
            <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${P.grad} flex items-center justify-center text-white font-bold text-sm shadow`}>{p.avatar}</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{p.name}</p>
              <p className="text-xs text-slate-500 truncate">{p.condition}</p>
              <p className="text-[11px] text-amber-700 mt-0.5 font-medium">{p.last}</p>
            </div>
            <span className="hidden sm:inline text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Inviato</span>
            <Check className="w-4 h-4 text-emerald-600" />
          </div>
        ))}
      </div>
      <div className="bg-slate-50 border-t border-slate-200 p-4">
        <p className="text-xs text-slate-600 text-center">Ogni cliente riceve un messaggio <strong>personalizzato</strong>. Il 25% torna entro 2 settimane.</p>
      </div>
    </div>
    <div className="mt-6 max-w-2xl mx-auto p-4 rounded-2xl bg-slate-900 text-white text-center">
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1 font-bold">Valore medio recuperato</p>
      <p className="text-2xl font-black text-emerald-400">{cfg.recallStat}</p>
      <p className="text-xs text-slate-400 mt-1">solo dai clienti dormienti riattivati</p>
    </div>
  </div>
  );
};

// ---------- STEP 7 — Packages ----------
const StepPackages = ({ cfg }) => {
  const P = cfg.palette;
  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-7`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold mb-4">
        <Wallet className="w-3 h-3" /> 7/10 · Pacchetti
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Vendi <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>pacchetti in un click</span>, traccia ogni credito</h2>
      <p className="text-slate-600 max-w-2xl mx-auto">{cfg.paksubtitle}</p>
    </div>
    <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
      {cfg.packages.map((pkg, i) => (
        <div key={i} className={`p-5 rounded-2xl border-2 ${i === 1 ? `${P.border} ${P.bg} shadow-xl scale-105` : "border-slate-200 bg-white"}`} data-testid={`demo-${cfg.key}-pkg-${i}`}>
          {i === 1 && <span className={`inline-block mb-3 text-[10px] uppercase tracking-wider font-bold bg-gradient-to-r ${P.grad} text-white px-2 py-0.5 rounded-full`}>Il più Acquistato</span>}
          <h3 className="font-bold text-lg mb-2">{pkg.name}</h3>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-black">€{pkg.price}</span>
            <span className="text-xs text-slate-400 line-through">€{pkg.price + pkg.savings}</span>
          </div>
          <p className="text-xs text-emerald-600 font-bold mb-4">Risparmio: €{pkg.savings}</p>
          <div className="space-y-1.5">
            {[`${pkg.sessions} sedute complete`, "Prenotabili quando vuoi", "Validità 6 mesi", "Saldo visibile in tempo reale"].map((f, j) => (
              <div key={j} className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Dashboard cliente · saldo pacchetto</p>
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${P.grad} flex items-center justify-center text-white font-bold shadow`}>
          {cfg.pkgClient.name.split(" ").map(s=>s[0]).join("")}
        </div>
        <div className="flex-1">
          <p className="font-bold">{cfg.pkgClient.name} · {cfg.pkgClient.pack}</p>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full bg-gradient-to-r ${P.grad}`} style={{width:"80%"}} /></div>
          <p className="text-xs text-slate-500 mt-1.5">8 sedute usate · <span className="text-amber-600 font-bold">2 rimaste</span></p>
        </div>
        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Proponi rinnovo</Button>
      </div>
    </div>
  </div>
  );
};

// ---------- STEP 8 — Analytics ----------
const StepAnalytics = ({ cfg }) => {
  const P = cfg.palette;
  const icons = [Users, Calendar, Wallet, TrendingUp, RefreshCw, Bell];
  const colors = [
    "from-sky-500 to-blue-600", "from-emerald-500 to-teal-600", "from-violet-500 to-purple-600",
    "from-amber-500 to-orange-600", "from-rose-500 to-pink-600", "from-indigo-500 to-blue-600",
  ];
  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid={`demo-${cfg.key}-step-8`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold mb-4">
        <BarChart3 className="w-3 h-3" /> 8/10 · I tuoi numeri, finalmente chiari
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Decidi sui <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>dati</span>, non a intuito</h2>
      <p className="text-slate-600 max-w-2xl mx-auto">Fatturato, riempimento, no-show, recall riattivati, servizi più redditizi — tutto nella stessa dashboard.</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-5xl mx-auto">
      {cfg.kpis.map((k, i) => {
        const Icon = icons[i];
        return (
          <div key={i} className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid={`demo-${cfg.key}-kpi-${i}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[i]} flex items-center justify-center shadow`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-[10px] font-bold text-emerald-600 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200">{k.trend}</span>
            </div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k.label}</p>
            <p className="text-2xl font-black text-slate-900">{k.value}</p>
          </div>
        );
      })}
    </div>
    <div className={`mt-8 max-w-3xl mx-auto p-6 rounded-3xl bg-gradient-to-br ${P.gradTo} text-white text-center shadow-2xl`}>
      <p className="text-xs uppercase tracking-wider opacity-80 mb-2 font-bold">In 3 mesi, un'attività media guadagna</p>
      <p className="text-5xl font-black mb-2">{cfg.finalGain}</p>
      <p className="text-sm opacity-90">recuperando no-show, riattivando clienti dormienti e vendendo più pacchetti</p>
    </div>
  </div>
  );
};

// ---------- STEP 9 — Closing ----------
const StepClosing = ({ cfg, clickTrial, clickCall }) => {
  const P = cfg.palette;
  return (
  <div className="animate-[fadeIn_0.5s_ease] py-6 sm:py-10" data-testid={`demo-${cfg.key}-step-9`}>
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
        <Sparkles className="w-3 h-3" /> Fine demo · Pronto a partire?
      </div>
      <h2 className="text-4xl sm:text-5xl font-black mb-4 leading-tight">
        Prova SmartBooker <span className={`bg-gradient-to-r ${P.gradTo} bg-clip-text text-transparent`}>14 giorni gratis</span>
      </h2>
      <p className="text-lg text-slate-600 max-w-2xl mx-auto">Setup guidato in <strong>3 minuti</strong>. Nessuna carta. Cancellazione istantanea.</p>
    </div>
    <div className={`max-w-2xl mx-auto bg-white rounded-3xl border-2 ${P.border} p-6 sm:p-8 shadow-xl`}>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {[
          "Agenda multi-operatore",
          "Prenotazioni online 24/7",
          "Reminder WhatsApp automatici",
          "Pacchetti con saldo automatico",
          "Recall clienti dormienti",
          "Analytics e report mensili",
          "Smart Page pubblica inclusa",
          "Pagamenti online integrati",
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${P.grad} flex items-center justify-center flex-shrink-0`}>
              <Check className="w-3 h-3 text-white" />
            </div>
            <span className="text-slate-700">{f}</span>
          </div>
        ))}
      </div>
      <Button
        onClick={() => clickTrial("closing")}
        size="lg"
        className={`w-full rounded-full bg-gradient-to-r ${P.grad} ${P.gradHover} text-white h-14 text-base font-bold shadow-xl`}
        data-testid={`demo-${cfg.key}-closing-trial`}
      >
        Inizia la prova gratuita di 14 giorni <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Nessuna carta</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Setup 3 min</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Supporto ITA</span>
      </div>
      <div className="mt-6 pt-6 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-500 mb-3">Preferisci una chiamata di 15 minuti con un consulente?</p>
        <a href="tel:+390000000000" onClick={() => clickCall("closing")}
          className={`inline-flex items-center gap-2 text-sm font-bold ${P.text} hover:opacity-80`}
          data-testid={`demo-${cfg.key}-closing-call`}>
          <PhoneCall className="w-4 h-4" /> Prenota una demo telefonica
        </a>
      </div>
    </div>
    <div className="mt-10 flex flex-wrap items-center justify-center gap-6 max-w-3xl mx-auto">
      {[
        { label: "Attività attive", value: "120+" },
        { label: "Servizi gestiti", value: "50.000+" },
        { label: "Clienti riattivati", value: "4.000+" },
        { label: "Rating", value: "4.9★" },
      ].map((s, i) => (
        <div key={i} className="text-center">
          <p className="text-2xl font-black text-slate-900">{s.value}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
  );
};

export default SectorDemoTemplate;
